import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Plus, Edit2, Trash2, ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySeasonalItem = {
  name: '',
  description: '',
  price: '',
  season: '',
  image_url: '',
  available_until: ''
};

export const AdminSeasonal = () => {
  const [seasonalItems, setSeasonalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptySeasonalItem);
  const [saving, setSaving] = useState(false);

  const fetchSeasonalItems = useCallback(async () => {
    try {
      const response = await fetch(`${API}/seasonal`);
      const data = await response.json();
      setSeasonalItems(data);
    } catch (error) {
      console.error('Failed to fetch seasonal items:', error);
      toast.error('Failed to load seasonal items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeasonalItems();
  }, [fetchSeasonalItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingItem 
        ? `${API}/admin/seasonal/${editingItem.seasonal_id}`
        : `${API}/admin/seasonal`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price)
        })
      });

      if (!response.ok) throw new Error('Failed to save seasonal item');

      toast.success(editingItem ? 'Seasonal item updated!' : 'Seasonal item added!');
      setIsDialogOpen(false);
      setEditingItem(null);
      setFormData(emptySeasonalItem);
      fetchSeasonalItems();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save seasonal item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (seasonalId) => {
    if (!confirm('Are you sure you want to delete this seasonal item?')) return;

    try {
      const response = await fetch(`${API}/admin/seasonal/${seasonalId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Seasonal item deleted!');
      fetchSeasonalItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete seasonal item');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      season: item.season,
      image_url: item.image_url || '',
      available_until: item.available_until || ''
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData(emptySeasonalItem);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-seasonal">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#CCFF00] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#CCFF00] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#CCFF00]" />
            <h1 className="font-permanent-marker text-3xl text-black">Manage Seasonal</h1>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openAddDialog}
                className="btn-graffiti-lime flex items-center gap-2"
                data-testid="add-seasonal-btn"
              >
                <Plus size={20} /> Add Seasonal
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-4 border-black rounded-xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bangers text-2xl">
                  {editingItem ? 'Edit Seasonal Item' : 'Add New Seasonal Item'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="seasonal-form">
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="Pumpkin Spice Delight"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="A warm, spiced blend perfect for fall..."
                    data-testid="input-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">Price *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="input-graffiti"
                      placeholder="7.99"
                      data-testid="input-price"
                    />
                  </div>
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">Season *</label>
                    <Input
                      value={formData.season}
                      onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                      required
                      className="input-graffiti"
                      placeholder="Fall"
                      data-testid="input-season"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Image</label>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Available Until</label>
                  <Input
                    value={formData.available_until}
                    onChange={(e) => setFormData({ ...formData, available_until: e.target.value })}
                    className="input-graffiti"
                    placeholder="December 31, 2025"
                    data-testid="input-available"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-4 py-2 border-4 border-black rounded-lg font-bangers text-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-graffiti-lime flex items-center justify-center gap-2"
                    data-testid="save-seasonal-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    Save
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Seasonal Items List */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#CCFF00] animate-spin mx-auto" />
          </div>
        ) : seasonalItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No seasonal items yet.</p>
              <p className="font-space-grotesk text-gray-500 mt-2">Add your first seasonal special!</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {seasonalItems.map((item, index) => (
              <motion.div
                key={item.seasonal_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-graffiti flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`seasonal-item-${index}`}
              >
                {item.image_url && (
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-black shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bangers text-xl text-black">{item.name}</h3>
                      <p className="font-space-grotesk text-gray-600 text-sm">{item.description}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-[#FF00FF] text-white text-xs font-space-grotesk font-semibold rounded border border-black">
                        {item.season}
                      </span>
                      {item.available_until && (
                        <span className="inline-block mt-1 ml-2 text-xs text-gray-500 font-space-grotesk">
                          Until {item.available_until}
                        </span>
                      )}
                    </div>
                    <div className="bg-[#CCFF00] px-3 py-1 rounded-full border-2 border-black font-bangers text-lg shrink-0">
                      ${item.price.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openEditDialog(item)}
                    className="p-2 bg-[#00FFFF] rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`edit-seasonal-${index}`}
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.seasonal_id)}
                    className="p-2 bg-red-500 text-white rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`delete-seasonal-${index}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSeasonal;
